import { RecordType } from '@xbcb/shared-types';
import { WorkOrderType } from '@xbcb/work-order-types';
import CustomsEntrySecondaryHeader from 'components/CustomsEntrySecondaryHeader';
import { CustomsDeclaration } from '../../../loadableComponents';
import {
  getSharedCustomsEntrySubmitButtons,
  sharedCustomsEntryKebabMenuItems,
} from '../../utils';
import { WorkOrderRoute } from '../.';
import { sendDocumentsToBroker } from './kebabMenuItems';

import {
  queryCustomsDeclaration,
  updateCustomsDeclaration,
} from './customsDeclaration.query';
import { transformCustomsDeclaration } from '../../../../libs/formTransforms/workOrder/customsDeclaration';
import { isCuDeCommercialInvoiceTabEnabled } from 'featureFlags';
import {
  getAllLiveGoodsShipmentFormRefs,
  getAllGoodsShipmentSnapshots,
} from 'libs/formCore/transforms/goodsShipmentFormRef';
import {
  getEditedGoodsItemsForShipment,
  setGoodsItemErrors,
  clearAllGoodsItemErrors,
} from 'libs/formCore/transforms/goodsItemFormRef';
import { goodsItemDetailSaveSchema } from 'libs/formCore/schemas/forms/goodsItemDetail';
import { goodsShipmentSaveSchema } from 'libs/formCore/schemas/forms/goodsShipment';
import { GoodsItemDetailTransformer } from 'libs/formCore/transforms/GoodsItemDetailTransformer';

const customsDeclarationRoute: WorkOrderRoute = {
  Page: CustomsDeclaration,
  recordType: RecordType.CUSTOMS_DECLARATION,
  queries: { get: queryCustomsDeclaration, update: updateCustomsDeclaration },
  SecondaryHeaderContents: [CustomsEntrySecondaryHeader],
  transformUpdateRecordInput: transformCustomsDeclaration.toSchema,
  defaultEditMode: isCuDeCommercialInvoiceTabEnabled,
  validateUpdate: async () => {
    const errors: { title: string; messages: string[] }[] = [];
    clearAllGoodsItemErrors();

    const liveRefs = getAllLiveGoodsShipmentFormRefs();
    const snapshots = getAllGoodsShipmentSnapshots();
    const itemTransformer = new GoodsItemDetailTransformer();

    for (const [idx, snapshot] of snapshots) {
      const liveRef = liveRefs.get(idx);

      // Validate shipment fields
      if (liveRef) {
        // Mounted: trigger inline errors via live form
        const isValid = await liveRef.validate();
        if (!isValid) {
          errors.push({
            title: `Goods Shipment ${idx + 1}`,
            messages: ['Please fix validation errors before saving.'],
          });
        }
      } else {
        // Unmounted: validate snapshot via Zod
        const result = goodsShipmentSaveSchema.safeParse(snapshot.formData);
        if (!result.success) {
          errors.push({
            title: `Goods Shipment ${idx + 1}`,
            messages: result.error.issues.map((i) => i.message),
          });
        }
      }

      // Validate goods items for this shipment
      const editedItems = getEditedGoodsItemsForShipment(idx);
      const allItems = snapshot.original.goodsItem ?? [];

      for (const item of allItems) {
        const sequence = item.sequence!;
        const editedEntry = editedItems.get(sequence);

        // Use edited form data if visited, otherwise round-trip through transformer
        const formData = editedEntry
          ? editedEntry.formData
          : itemTransformer.toForm(item);

        const result = goodsItemDetailSaveSchema.safeParse(formData);
        if (!result.success) {
          const fieldErrors: Record<string, { message: string }> = {};
          for (const issue of result.error.issues) {
            const path = issue.path.join('.');
            if (!fieldErrors[path]) {
              fieldErrors[path] = { message: issue.message };
            }
          }
          setGoodsItemErrors(idx, sequence, fieldErrors as any);
          if (editedEntry) editedEntry.applyErrors?.(fieldErrors as any);
          errors.push({
            title: `Goods Item ${sequence} (Shipment ${idx + 1})`,
            messages: result.error.issues.map((i) => i.message),
          });
        }
      }
    }

    return errors.length ? { additionalErrors: errors } : {};
  },
  kebabMenuItems: [...sharedCustomsEntryKebabMenuItems, sendDocumentsToBroker],
  submitButtons: getSharedCustomsEntrySubmitButtons(
    WorkOrderType.CustomsDeclaration,
  ),
};

export default customsDeclarationRoute;
